import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'Events', href: '/events' },
  { label: 'About Us', href: { pathname: '/', hash: '#about' } },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Podcast and Videos', href: '/podcast-interviews' },
  { label: 'Reviews', href: '/reviews' },
  { label: 'Contact', href: '/contact' },
];

function handleHashNavClick(event, href, pathname, navigate) {
  if (typeof href !== 'object' || !href.hash) return;

  const targetPath = href.pathname ?? '/';
  if (pathname !== targetPath) return;

  event.preventDefault();
  navigate({ pathname: targetPath, hash: href.hash });
}

const supportLinks = [
  { label: 'FAQs', href: '/#faq' },
  { label: 'Terms & Conditions', href: '/#terms' },
  { label: 'Privacy Policy', href: '/#privacy' },
  { label: 'Refund Policy', href: '/#refund' },
];

export default function Footer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <footer className="bg-secondary border-t border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="inline-flex mb-4">
              <AppLogo size={120} />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mb-5">
              Crafting memorable entertainment experiences for the Sri Lankan community in Australia.
            </p>
            <div className="flex items-center gap-4">
              {[
                { label: 'Facebook', icon: 'GlobeAltIcon' },
                { label: 'Instagram', icon: 'CameraIcon' },
                { label: 'YouTube', icon: 'PlayCircleIcon' },
                { label: 'TikTok', icon: 'MusicalNoteIcon' },
              ].map((s) => (
                <button
                  key={s.label}
                  aria-label={s.label}
                  className="social-icon-hover w-9 h-9 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground"
                >
                  <Icon name={s.icon} size={16} variant="outline" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-bold tracking-widest uppercase text-primary mb-5">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    onClick={(event) => handleHashNavClick(event, link.href, pathname, navigate)}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold tracking-widest uppercase text-primary mb-5">
              Support
            </h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold tracking-widest uppercase text-primary mb-5">
              Contact Us
            </h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <Icon name="PhoneIcon" size={15} className="text-primary shrink-0" />
                <a href="tel:+61404706121" className="hover:text-primary transition-colors">+61 404 706 121</a>
              </li>
              <li className="flex items-center gap-2.5 text-sm text-muted-foreground">
                <Icon name="EnvelopeIcon" size={15} className="text-primary shrink-0" />
                <a href="mailto:info@awakasha.com.au" className="hover:text-primary transition-colors">
                  info@awakasha.com.au
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-muted-foreground">
                <Icon name="MapPinIcon" size={15} className="text-primary shrink-0 mt-0.5" />
                <span>Melbourne, Australia</span>
              </li>
            </ul>

            <div className="partner-badge mt-6 inline-flex flex-col gap-2">
              <Link
                to="/events"
                className="btn-gold flex items-center justify-center gap-1.5 px-4 py-2 rounded text-xs font-bold tracking-wider uppercase mt-1"
              >
                <span>Book Tickets</span>
                <Icon name="ArrowRightIcon" size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border/40 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            © 2026 Awakasha Entertainment. All Rights Reserved.
          </p>
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <Link to="/#privacy" className="hover:text-primary transition-colors">
              Privacy
            </Link>
            <Link to="/#terms" className="hover:text-primary transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
